import React from 'react';
import { Link } from 'react-router-dom';
import './TermsConditionsPage.css';

const privacySections = [
  ['information-collected', '1. Information We Collect'],
  ['information-use', '2. How We Use Information'],
  ['forms-providers', '3. Forms and Service Providers'],
  ['analytics-cookies', '4. Analytics and Cookies'],
  ['information-sharing', '5. When We Share Information'],
  ['retention', '6. Data Retention'],
  ['security', '7. Data Security'],
  ['rights-choices', '8. Your Rights and Choices'],
  ['children', "9. Children's Privacy"],
  ['external-links', '10. External Links'],
  ['policy-changes', '11. Changes to This Policy'],
  ['contact', '12. Contact'],
];

const PrivacyPolicyPage = () => {
  const scrollToPrivacySection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <main className="terms-page privacy-page">
      <section className="terms-hero">
        <p className="terms-eyebrow">Teamward Legal</p>

        <h1>Privacy Policy</h1>

        <p className="terms-intro">
          This Privacy Policy explains how Teamward collects, uses, and shares
          information when you visit teamward.ai, submit a form, request
          recommendations or planning assistance, or apply to become a partner.
        </p>

        <p className="terms-effective-date">
          Effective date: August 24, 2026
        </p>
      </section>

      <div className="terms-layout">
        <aside className="terms-sidebar">
          <p className="terms-sidebar-title">On this page</p>

          <nav aria-label="Privacy Policy sections">
            {privacySections.map(([sectionId, label]) => (
              <button
                key={sectionId}
                type="button"
                onClick={() => scrollToPrivacySection(sectionId)}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <article className="terms-content">
          <div className="terms-notice">
            <strong>You do not need an account to browse Teamward.</strong>

            <p>
              We collect information you choose to submit and limited
              technical information about how visitors use the website.
            </p>
          </div>

          <section
            id="information-collected"
            className="terms-section"
          >
            <span className="terms-number">01</span>
            <h2>Information We Collect</h2>

            <p>
              The information Teamward collects depends on how you use the
              website.
            </p>

            <p>
              <strong>Information you provide directly may include:</strong>
            </p>

            <ul>
              <li>Your name, email address, and telephone number</li>
              <li>Your company, organization, job title, or team</li>
              <li>
                Event details such as location, preferred dates, group size,
                budget, goals, and preferences
              </li>
              <li>
                Experiences, venues, trainings, speakers, or services that
                interest you
              </li>
              <li>
                Questions, messages, and other information included in a
                request
              </li>
              <li>
                Business information submitted through a partner application
              </li>
              <li>
                Information exchanged during planning, coordination, or
                partnership communications
              </li>
            </ul>

            <p>
              <strong>Information collected automatically may include:</strong>
            </p>

            <ul>
              <li>Internet Protocol address</li>
              <li>Browser type and device type</li>
              <li>Operating system</li>
              <li>Approximate location derived from an IP address</li>
              <li>Pages viewed and links clicked</li>
              <li>Date, time, and duration of a visit</li>
              <li>Referring website or campaign information</li>
              <li>Website-performance and diagnostic information</li>
            </ul>

            <p>
              Teamward does not currently collect payment-card information
              directly through this website.
            </p>

            <p>
              Please do not submit Social Security numbers, financial-account
              information, medical information, or other sensitive personal
              information through Teamward forms.
            </p>
          </section>

          <section id="information-use" className="terms-section">
            <span className="terms-number">02</span>
            <h2>How We Use Information</h2>

            <p>Teamward may use information to:</p>

            <ul>
              <li>Respond to questions and requests</li>
              <li>Provide personalized team-experience recommendations</li>
              <li>Identify suitable venues, activities, or providers</li>
              <li>Request availability, pricing, or service information</li>
              <li>
                Prepare planning options, proposals, or coordination services
              </li>
              <li>Process and review partner applications</li>
              <li>Communicate about a requested service or partnership</li>
              <li>Operate, maintain, troubleshoot, and improve the website</li>
              <li>Measure website traffic and engagement</li>
              <li>Prevent fraud, misuse, or security incidents</li>
              <li>Comply with legal obligations</li>
              <li>Protect Teamward, its users, and its partners</li>
            </ul>

            <p>
              Providing information through a form does not automatically
              enroll you in unrelated marketing communications.
            </p>
          </section>

          <section id="forms-providers" className="terms-section">
            <span className="terms-number">03</span>
            <h2>Forms and Service Providers</h2>

            <p>
              Teamward uses Formspace to host embedded forms and process form
              responses.
            </p>

            <p>
              When you submit a Teamward form, the information is transmitted
              to Formspace, stored through its service, and made available to
              Teamward so we can respond to your request.
            </p>

            <p>
              Formspace generally processes customer-directed form content on
              behalf of the customer that created the form. You can learn more
              in the{' '}
              <a
                href="https://formspace.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Formspace Privacy Policy
              </a>
              .
            </p>

            <p>
              Teamward also uses service providers for website hosting,
              analytics, email, data storage, security, and other business
              operations. These providers may access information only as
              reasonably necessary to perform services for Teamward.
            </p>

            <p>
              Service providers may process information in locations outside
              your state or country. Privacy laws may differ between those
              locations.
            </p>
          </section>

          <section id="analytics-cookies" className="terms-section">
            <span className="terms-number">04</span>
            <h2>Google Analytics and Cookies</h2>

            <p>
              Teamward uses Google Analytics to understand how visitors find
              and interact with the website.
            </p>

            <p>
              Google Analytics may use cookies and similar technologies to
              collect information such as:
            </p>

            <ul>
              <li>Pages viewed</li>
              <li>Links clicked</li>
              <li>Referring websites</li>
              <li>Browser and device information</li>
              <li>IP address and approximate location</li>
              <li>Visit dates, times, and duration</li>
            </ul>

            <p>
              Teamward uses this information to measure website performance,
              identify useful content, and improve the visitor experience.
              Teamward does not intentionally send names, email addresses, or
              form responses to Google Analytics.
            </p>

            <p>
              Teamward does not currently use Google Analytics for remarketing
              or personalized advertising.
            </p>

            <p>
              You can learn more about how Google processes information from
              websites using Google services by visiting{' '}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
              >
                How Google uses information from sites or apps that use its
                services
              </a>
              .
            </p>

            <p>
              You can control or delete cookies through your browser settings.
              Blocking cookies may affect some website features or analytics.
            </p>
          </section>

          <section id="information-sharing" className="terms-section">
            <span className="terms-number">05</span>
            <h2>When We Share Information</h2>

            <p>Teamward may share information:</p>

            <ul>
              <li>
                With venues, trainers, speakers, caterers, activity providers,
                or other vendors when needed to respond to your request
              </li>
              <li>
                To request availability, pricing, proposals, or service details
              </li>
              <li>
                With service providers that support Teamward’s website, forms,
                analytics, communications, storage, or security
              </li>
              <li>
                When required by law, legal process, or a valid government
                request
              </li>
              <li>
                When reasonably necessary to investigate fraud, misuse,
                security issues, or threats
              </li>
              <li>
                As part of a merger, acquisition, financing, or transfer of
                Teamward’s business or assets
              </li>
              <li>With your consent or at your direction</li>
            </ul>

            <p>
              Teamward may receive referral compensation or commissions from
              certain providers if a booking results. This does not authorize a
              provider to use your information for unrelated marketing.
            </p>

            <p>
              Teamward does not sell personal information to data brokers or
              share personal information for cross-context behavioral
              advertising.
            </p>
          </section>

          <section id="retention" className="terms-section">
            <span className="terms-number">06</span>
            <h2>Data Retention</h2>

            <p>
              Teamward retains personal information only for as long as
              reasonably necessary for the purposes described in this Policy.
            </p>

            <p>Retention may depend on:</p>

            <ul>
              <li>The status of an inquiry or planning request</li>
              <li>An ongoing customer or partner relationship</li>
              <li>Business-record and accounting requirements</li>
              <li>Legal, contractual, or regulatory obligations</li>
              <li>Security, fraud-prevention, or dispute-resolution needs</li>
            </ul>

            <p>
              When information is no longer reasonably needed, Teamward may
              delete, anonymize, or securely archive it where practical.
            </p>
          </section>

          <section id="security" className="terms-section">
            <span className="terms-number">07</span>
            <h2>Data Security</h2>

            <p>
              Teamward uses reasonable administrative and technical measures
              intended to protect personal information against unauthorized
              access, loss, misuse, or disclosure.
            </p>

            <p>
              However, no website, electronic transmission, or storage system
              can be guaranteed to be completely secure. You submit information
              through the website at your own risk.
            </p>
          </section>

          <section id="rights-choices" className="terms-section">
            <span className="terms-number">08</span>
            <h2>Your Rights and Choices</h2>

            <p>
              Depending on where you live and subject to applicable law, you
              may have the right to:
            </p>

            <ul>
              <li>Request access to personal information about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of information</li>
              <li>Object to or restrict certain uses of information</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Opt out of marketing communications</li>
            </ul>

            <p>
              To submit a privacy request, email{' '}
              <a href="mailto:mkuhl@teamward.ai">
                mkuhl@teamward.ai
              </a>
              . Teamward may need to verify your identity before completing a
              request.
            </p>

            <p>
              Some information may be retained when required by law or when
              reasonably necessary for security, fraud prevention, disputes,
              recordkeeping, or the establishment or defense of legal claims.
            </p>
          </section>

          <section id="children" className="terms-section">
            <span className="terms-number">09</span>
            <h2>Children’s Privacy</h2>

            <p>
              Teamward is intended for businesses and adults arranging team
              experiences. The website is not directed to children under 13,
              and Teamward does not knowingly collect personal information
              directly from children under 13.
            </p>

            <p>
              If you believe a child has submitted personal information through
              Teamward, please contact us so we can review and, where
              appropriate, delete it.
            </p>
          </section>

          <section id="external-links" className="terms-section">
            <span className="terms-number">10</span>
            <h2>External Links</h2>

            <p>
              Teamward may link to venues, vendors, booking systems, forms, and
              other third-party websites.
            </p>

            <p>
              Teamward does not control those websites or their privacy
              practices. Information you provide directly to a third party is
              governed by that third party’s privacy policy.
            </p>
          </section>

          <section id="policy-changes" className="terms-section">
            <span className="terms-number">11</span>
            <h2>Changes to This Policy</h2>

            <p>Teamward may update this Privacy Policy periodically.</p>

            <p>
              Updates will be posted on this page with a revised effective
              date. Material changes may also be communicated through the
              website or other appropriate methods.
            </p>
          </section>

          <section
            id="contact"
            className="terms-section terms-contact-section"
          >
            <span className="terms-number">12</span>
            <h2>Contact</h2>

            <p>
              Questions or requests concerning this Privacy Policy may be sent
              to:
            </p>

            <div className="terms-contact-card">
              <strong>Teamward</strong>

              <a href="mailto:mkuhl@teamward.ai">
                mkuhl@teamward.ai
              </a>

              <a
                href="https://teamward.ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                teamward.ai
              </a>
            </div>
          </section>

          <div className="terms-bottom-navigation">
            <Link to="/">Return Home</Link>
            <Link to="/terms-conditions">Terms of Use</Link>
          </div>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
